<template>
  <div style="margin: 0 16px; display: flex; flex-direction: row; justify-content: center;">
    <div>
      <form @submit.prevent="login" data-tour="login-form">
        <h3>Entrar</h3>

        <label for="username">Usuário</label>
        <input data-tour="login-email" type="email" placeholder="E-mail" id="email" name="email" required autofocus v-model="email">

        <label for="password">Senha</label>
        <div class="password-container">
          <input data-tour="login-password" :type="passwordFieldType" placeholder="Password" id="password" name="password" required v-model="password">
          <img :src="eyeIcon" @click="togglePasswordVisibility" class="eye-icon">
        </div>

        <button data-tour="login-submit">Entrar</button>

        <button data-tour="login-reset" type="button" @click="resetPassword" style="margin-top: 40px;">Esqueci a senha</button>
      </form>
    </div>
  </div>

   <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <v-card-text>
        {{ modalMessage }}
      </v-card-text>
      <v-card-actions>
        <v-btn style="display: flex; align-items: center;" color="green darken-1" text @click="dialog = false">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import { ref, onMounted, watch, nextTick } from 'vue'
  import { useStore } from 'vuex'
  import { sendPasswordResetEmail } from "firebase/auth";
  import { useRouter, useRoute } from 'vue-router'
import { driver } from 'driver.js'
  import { auth } from '@/config/firebase'
  import { collection, getDocs, doc, getDoc } from 'firebase/firestore'
  import { getFirestore } from 'firebase/firestore'
  import { checarVersaoFrontend } from "@/config/firebaseVersionCheck";

  export default {
    inheritAttrs: false,
    name: "LoginComponent",
    setup() {
      const email = ref('')
      const password = ref('')
      const error = ref(null)
      const passwordFieldType = ref('password')
      const eyeIcon = ref(require('@/assets/eye_icon.png'))
      const dialog = ref(false)
      const modalMessage = ref('')

      const store = useStore()
      const router = useRouter()
      const route = useRoute()
      const db = getFirestore()
      const normalizeSlug = (value) => (value || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
      const resolveAccountId = (slug) => `acc_${normalizeSlug(slug)}`

      const startLoginTourFromQuery = async () => {
        if (route.query.tour !== 'login') return;
        await nextTick();
        startLoginTour();
      };

      const startLoginTour = () => {
        const steps = [
          {
            element: '[data-tour="login-form"]',
            popover: {
              title: 'Acesso',
              description: 'Preencha seus dados para entrar na plataforma.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: '[data-tour="login-email"]',
            popover: {
              title: 'E-mail',
              description: 'Use o e-mail cadastrado para sua empresa.',
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-tour="login-password"]',
            popover: {
              title: 'Senha',
              description: 'Digite sua senha de acesso.',
              side: 'right',
              align: 'start',
            },
          },
          {
            element: '[data-tour="login-submit"]',
            popover: {
              title: 'Entrar',
              description: 'Clique para iniciar sua sessão.',
              side: 'bottom',
              align: 'center',
            },
          },
          {
            element: '[data-tour="login-reset"]',
            popover: {
              title: 'Recuperar senha',
              description: 'Use esse botão caso tenha esquecido sua senha.',
              side: 'top',
              align: 'center',
            },
          },
        ].filter(step => document.querySelector(step.element));

        if (!steps.length) return;

        const tour = driver({
          showProgress: true,
          steps,
        });
        tour.drive();
      };

      onMounted(async () => {
        await checarVersaoFrontend();
        await startLoginTourFromQuery();
      });

      watch(() => route.query.tour, () => {
        startLoginTourFromQuery();
      });

      const login = async () => {
        try {
          const normalizedEmail = (email.value || '').trim().toLowerCase();
          if (!normalizedEmail) {
            modalMessage.value = 'Digite um e-mail para entrar';
            dialog.value = true;
            throw new Error('E-mail vazio');
          }

          await store.dispatch('logIn', {
            email: normalizedEmail,
            password: password.value,
          })

          store.commit('SET_EMAIL', normalizedEmail);
          const companyName = await checkUserCompany(normalizedEmail).catch(() => null);
          if (companyName) {
            router.push(`/${companyName}`);
            return;
          }
          router.push('/me');
        } catch (err) {
          const msg = err && err.message ? err.message : '';
          const code = (err && err.code ? err.code : '').toString();
          if (
            code === 'auth/invalid-credential' ||
            code === 'auth/invalid-login-credentials' ||
            code === 'auth/wrong-password' ||
            msg.includes('auth/invalid-credential') ||
            msg.includes('auth/invalid-login-credentials') ||
            msg.includes('auth/wrong-password')
          ) {
            modalMessage.value = 'Senha incorreta. Por favor, tente novamente.';
          } else if (msg.includes('auth/too-many-requests')) {
            modalMessage.value = 'Muitas tentativas de login. Por favor, aguarde um momento e tente novamente.';
          } else if (!modalMessage.value) {
            modalMessage.value = 'Falha no login. Verifique suas credenciais.';
          }
          dialog.value = true;
        }
      }

      const checkUserCompany = async (email) => {
        const emailKey = (email || '').trim().toLowerCase();
        // Se a rota contiver o cliente, checa direto nele
        const slug = (route.params.cliente || route.params.company || '').toString().trim().toLowerCase();
        if (slug) {
          const userDocRef = doc(db, `accounts/${resolveAccountId(slug)}/users/${emailKey}`);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists() && userDoc.data()?.approved === true) return slug;
          return null;
        }
        // Caso generico: varre colecao canonica 'accounts'
        const companyRef = collection(db, 'accounts');
        const companySnapshot = await getDocs(companyRef);
        for (const companyDoc of companySnapshot.docs) {
          const companyData = companyDoc.data() || {};
          const companyName = (companyData.slug || companyData.legacy_cliente_slug || companyDoc.id.replace(/^acc_/, '')).toString().toLowerCase();
          const userDocRef = doc(db, `accounts/${companyDoc.id}/users/${emailKey}`);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists() && userDoc.data()?.approved === true) {
            return companyName;
          }
        }
        return null;
      };

      const resetPassword = async () => {
        if (!email.value) {
          modalMessage.value = 'Por favor, insira seu e-mail para redefinição de senha.';
          dialog.value = true;
          return
        }
        try {
          const normalizedEmail = (email.value || '').trim().toLowerCase();
          await sendPasswordResetEmail(auth, normalizedEmail)
          modalMessage.value = 'E-mail de redefinição de senha enviado com sucesso!';
          dialog.value = true;
        } catch (err) {
          const code = (err && err.code ? err.code : '').toString();
          if (code === 'auth/invalid-email') {
            modalMessage.value = 'E-mail inválido. Verifique e tente novamente.';
          } else {
            modalMessage.value = 'Erro ao enviar e-mail de redefinição de senha. Tente novamente.';
          }
          dialog.value = true;
        }
      }

      const togglePasswordVisibility = () => {
        if (passwordFieldType.value === 'password') {
          passwordFieldType.value = 'text'
        } else {
          passwordFieldType.value = 'password'
        }
      }

      return { email, password, error, login, resetPassword, passwordFieldType, eyeIcon, togglePasswordVisibility, dialog, modalMessage }
    },
  }
</script>


<style scoped>
  *,
*:before,
*:after {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

body {
  background-color: #080710;
}

.background {
  width: 430px;
  height: 620px;
  position: absolute;
  transform: translate(-50%, -50%);
  left: 50%;
  top: 50%;
}

.background .shape {
  height: 200px;
  width: 200px;
  position: absolute;
  border-radius: 50%;
}

.shape:first-child {
  background: linear-gradient(
    #1845ad,
    #23a2f6
  );
  left: -80px;
  top: -80px;
}

.shape:last-child {
  background: linear-gradient(
    to right,
    #035762,
    #09bfd7
  );
  right: -30px;
  bottom: -80px;
}

form {
  margin-top: 32px;
  min-height: 520px;
  width: 400px;
  background-color: rgba(255, 255, 255, 0.13);
  position: relative;
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 8px 8px 10px 8px #baccde8a;
  padding: 50px 35px;
}

form * {
  font-family: 'Poppins', sans-serif;
  color: #191e23;
  letter-spacing: 0.5px;
  outline: none;
  border: none;
}

form h3 {
  font-size: 32px;
  font-weight: 500;
  line-height: 42px;
  text-align: center;
}

label {
  display: block;
  margin-top: 30px;
  font-size: 16px;
  font-weight: 500;
}

input {
  display: block;
  height: 50px;
  width: 100%;
  background-color: rgba(255, 255, 255);
  border-radius: 3px;
  padding: 0 10px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 300;
}

::placeholder {
  color: #191e23;
}

button {
  margin-top: 50px;
  width: 100%;
  background-color: #191e23;
  color: white;
  padding: 15px 0;
  font-size: 18px;
  font-weight: 600;
  border-radius: 5px;
  cursor: pointer;
}

.social {
  margin-top: 30px;
  display: flex;
}

.social div {
  width: 150px;
  border-radius: 3px;
  padding: 5px 10px 10px 5px;
  background-color: rgba(255, 255, 255, 0.27);
  color: #eaf0fb;
  text-align: center;
}

.social div:hover {
  background-color: rgba(255, 255, 255, 0.47);
}

.social .fb {
  margin-left: 25px;
}

.social i {
  margin-right: 4px;
}

.password-container {
  position: relative;
}

.eye-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 20px;
  height: 20px;
}

@media (max-width: 768px) {
  form { margin-top: 24px; width: 100%; }
}


</style>




